import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Link2, Plus, Trash2 } from 'lucide-react';
import { RELATION_KINDS, RELATION_LABELS, RELATION_LOCKED, RELATION_LOCKED_MESSAGE, type AssetDetail, type RelationKind } from '@prism/contracts';

import { api, errorMessage } from '../api/client';
import { useCategories, useGenealogy, useLibrary, useMe, useRelations } from '../api/hooks';
import { toast } from '../components/Toasts';
import { CategoryPill } from './library/LibraryItems';
import { queryKeys } from '../api/queryKeys';
import { GenealogySections } from './GenealogySections';

/** Choices as they read from this asset: e.g. "Part of" (this → other) or "Contains" (other → this). */
type Choice = { value: string; kind: RelationKind; direction: 'from' | 'to'; label: string };
const CHOICES: Choice[] = RELATION_KINDS.flatMap((kind): Choice[] =>
  kind === 'related'
    ? [{ value: 'related:from', kind, direction: 'from' as const, label: RELATION_LABELS.related.from }]
    : [
        { value: `${kind}:from`, kind, direction: 'from' as const, label: RELATION_LABELS[kind].from },
        { value: `${kind}:to`, kind, direction: 'to' as const, label: RELATION_LABELS[kind].to },
      ]);

/** The asset's class, changeable in place by administrators and media managers. */
export function CategoryPicker({ asset }: { asset: AssetDetail }) {
  const cats = useCategories().data?.items ?? [];
  const me = useMe().data!;
  const qc = useQueryClient();
  const canEdit = me.permissions.includes('asset.classify') && !asset.deleted;
  return (
    <select className="speed-select" aria-label="Asset class" data-testid="asset-category" disabled={!canEdit} value={asset.category?.id ?? ''}
      style={asset.category ? { color: asset.category.color, borderColor: asset.category.color } : undefined}
      onChange={async (e) => {
        try {
          await api(`/api/v1/assets/${asset.id}/category`, { method: 'PUT', json: { categoryId: e.target.value || null } });
          await qc.invalidateQueries({ queryKey: queryKeys.asset(asset.id) });
          await qc.invalidateQueries({ queryKey: queryKeys.assets() });
        } catch (err) {
          toast(errorMessage(err), 'error');
        }
      }}>
      <option value="">No class</option>
      {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}

export function RelationsTab({ asset }: { asset: AssetDetail }) {
  const q = useRelations(asset.id);
  const me = useMe().data!;
  const qc = useQueryClient();
  const genealogy = useGenealogy(asset.id).data;
  const hasGenealogy = Boolean(genealogy && (genealogy.madeFrom.length || genealogy.usedIn.length || genealogy.hiddenCount));
  const canEdit = me.permissions.includes('asset.edit_metadata') && !asset.deleted;
  const [adding, setAdding] = useState(false);
  const [choice, setChoice] = useState('part_of:from');
  const [search, setSearch] = useState('');
  const [note, setNote] = useState('');
  const results = useLibrary({ q: search, status: [], kind: [], location: [], category: [], fields: {}, sort: 'newest', includeDismissed: false });
  // 1.8.0: the links PRISM recorded when an asset was made (origin system) are in Made from and Used in, not here.
  const items = (q.data?.items ?? []).filter((r) => r.origin !== 'system');
  const groups = [...new Set(items.map((r) => r.label))];

  async function add(otherAssetId: string) {
    const c = CHOICES.find((x) => x.value === choice)!;
    try {
      await api(`/api/v1/assets/${asset.id}/relations`, { method: 'POST', json: { otherAssetId, kind: c.kind, direction: c.direction, ...(note.trim() ? { note: note.trim() } : {}) } });
      await qc.invalidateQueries({ queryKey: ['relations'] });
      setAdding(false);
      setSearch('');
      setNote('');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  async function remove(id: string) {
    try {
      await api(`/api/v1/assets/${asset.id}/relations/${id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['relations'] });
    } catch (err) {
      toast((err as { code?: string }).code === RELATION_LOCKED ? RELATION_LOCKED_MESSAGE : errorMessage(err), 'error');
    }
  }

  return (
    <div data-testid="relations-tab" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <GenealogySections assetId={asset.id} />
      {items.length === 0 && !adding && <p className="muted" style={{ margin: 0 }}>{hasGenealogy ? 'No other related assets.' : 'Not related to other assets yet.'} Relate clips to their programme, trailers to what they promote, or edits to the footage they use.</p>}
      {groups.map((g) => (
        <div key={g}>
          <p className="section-label">{g}</p>
          {items.filter((r) => r.label === g).map((r) => (
            <div className="loc-row" key={r.id} data-testid="relation">
              <Link2 size={13} className="faint" />
              <Link to={`/assets/${r.other.id}`} style={{ fontWeight: 550 }}>{r.other.title}</Link>
              {r.other.category && <CategoryPill category={r.other.category} />}
              {r.other.deleted && <span className="pill">In recycle bin</span>}
              <span className="key">{r.note ?? ''}</span>
              {canEdit && r.origin !== 'system' && <button className="btn ghost icon sm" aria-label="Remove relation" onClick={() => void remove(r.id)}><Trash2 size={12} /></button>}
            </div>
          ))}
        </div>
      ))}
      {canEdit && !adding && <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => setAdding(true)} data-testid="add-relation"><Plus size={13} /> Relate to another asset</button>}
      {adding && (
        <div className="panel-card">
          <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
            <div className="field"><label htmlFor="rel-kind">This asset is</label>
              <select id="rel-kind" className="select" value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="relation-kind">
                {CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select></div>
            <div className="field"><label htmlFor="rel-search">Asset</label>
              <input id="rel-search" className="input" autoFocus placeholder="Search by title or file name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="relation-search" /></div>
          </div>
          <div className="field"><label htmlFor="rel-note">Note (optional)</label><input id="rel-note" className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 240, overflow: 'auto' }}>
            {(results.data?.items ?? []).filter((a) => a.id !== asset.id).slice(0, 20).map((a) => (
              <button key={a.id} className="folder-row" onClick={() => void add(a.id)} data-testid="relation-candidate">
                <span style={{ fontWeight: 550 }}>{a.title}</span>
                {a.category && <CategoryPill category={a.category} />}
                <span className="faint" style={{ marginLeft: 'auto', fontSize: 12 }}>{a.originalFilename}</span>
              </button>
            ))}
          </div>
          <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={() => setAdding(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}
